import { getUserInfo } from "@/api/login";
import type { User } from "@/api/login";

/**
 * Chatwoot 网页/客户端客服接入
 *
 * 传给后端 / 关联方式：
 * - 我们传：websiteToken（渠道）、identifier（当前登录用户 userNo）、name（userName 或 userNo），
 *   后台用 identifier 识别同一用户，同一 identifier 下的多轮对话会归为同一 contact 的会话列表。
 * - 对话是否「永远只有一个」：按 contact 维度，一个 contact 可有多个会话（例如客服解析后用户再发会开新会话）；
 *   是否展示「开启新对话」由 Chatwoot 后台/Widget 配置决定，前端未单独传「开新会话」参数。
 *
 * 约定：在前端插入一段 script。下面 loadChatwoot() 里的逻辑，等价于官方/后台给的这段「复制粘贴」代码：
 *
 *   window.chatwootSettings = { hideMessageBubble: true };
 *   (function(d,t){
 *     var BASE_URL = "https://你的chatwoot域名";
 *     var g = d.createElement(t), s = d.getElementsByTagName(t)[0];
 *     g.src = BASE_URL + "/packs/js/sdk.js";
 *     g.async = true; g.defer = true;
 *     s.parentNode.insertBefore(g, s);
 *     g.onload = function(){
 *       window.chatwootSDK.run({ websiteToken: '你的token', baseUrl: BASE_URL });
 *     };
 *   })(document, "script");
 *
 * 我们没有在项目里保存上面这段字符串，而是用 loadChatwoot() 里的代码在运行时做同样的事：
 * 创建 <script src="baseUrl/packs/js/sdk.js">、onload 时 run({ websiteToken, baseUrl })。
 * baseUrl 和 websiteToken 从环境变量读；你若希望改成「后端接口返回整段 script 字符串再插入」，可以再改。
 */

const BASE_URL = (process.env.VUE_APP_CHATWOOT_BASE_URL || "").replace(
  /\/$/,
  ""
);
const WEBSITE_TOKEN = process.env.VUE_APP_CHATWOOT_WEBSITE_TOKEN || "";

export const isChatwootEnabled = Boolean(BASE_URL && WEBSITE_TOKEN);

let loadStarted = false;
let sdkScriptEl: HTMLScriptElement | null = null;
let readySyncHandler: (() => void) | null = null;

function tokenDebugSnapshot() {
  const token = localStorage.getItem("token") || "";
  const isLogin = localStorage.getItem("isLogin") || "";
  const userInfo = localStorage.getItem("userInfo") || "";
  return {
    hasToken: token.length > 0,
    tokenLength: token.length,
    isLogin,
    hasUserInfo: userInfo.length > 0,
  };
}

function removeReadySyncHandler() {
  if (readySyncHandler) {
    window.removeEventListener("chatwoot:ready", readySyncHandler);
    readySyncHandler = null;
  }
}

function removeChatwootDomArtifacts() {
  const selectors = [
    "#chatwoot_live_chat_widget",
    "#cw-widget-holder",
    "#cw-widget-container",
    "#cw-notifications-holder",
  ];

  selectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => el.remove());
  });
}

/** 在前端插入 Chatwoot script：创建 script 标签并 append 到 body，等价于上面注释里的 IIFE 片段 */
export function loadChatwoot(): void {
  if (!isChatwootEnabled || loadStarted) return;
  loadStarted = true;

  // 实时收消息需服务端 WebSocket 正常：反向代理要正确转发 Connection: Upgrade / Upgrade: websocket，参见 https://github.com/chatwoot/chatwoot/issues/10557
  const isElectron = Boolean(process.env.IS_ELECTRON);
  window.chatwootSettings = {
    hideMessageBubble: true,
    // Electron 中禁用 popout，避免触发 window.open 相关的不稳定行为
    showPopoutButton: !isElectron,
  };

  const script = document.createElement("script");
  script.src = `${BASE_URL}/packs/js/sdk.js`;
  script.async = true;
  script.defer = true;
  script.dataset.chatwoot = "sdk";
  script.onload = () => {
    if (window.chatwootSDK) {
      window.chatwootSDK.run({
        websiteToken: WEBSITE_TOKEN,
        baseUrl: BASE_URL,
      });
    }
    removeReadySyncHandler();
    readySyncHandler = () => syncChatwootUser();
    window.addEventListener("chatwoot:ready", readySyncHandler, {
      once: true,
    });
  };
  script.onerror = (err) => {
    console.error("[chatwoot] sdk script load failed", err);
    loadStarted = false;
    sdkScriptEl = null;
  };
  document.body.appendChild(script);
  sdkScriptEl = script;
}

/**
 * 登出时调用，清空 Chatwoot 会话，下次登录会以新身份 setUser。
 */
export function resetChatwootSession(): void {
  if (
    isChatwootEnabled &&
    window.$chatwoot &&
    typeof window.$chatwoot.reset === "function"
  ) {
    window.$chatwoot.reset();
  }
}

/**
 * 前端通过 Widget SDK 可配置的 contact 属性（setUser 第二参数）：
 * - name：展示名（必填或与 identifier 一起才有意义）
 * - email：邮箱
 * - avatar_url：头像图片 URL
 * - phone_number：手机号
 * - identifier_hash：HMAC 校验用（后台开启「强制身份校验」时需传）
 * - description：简介
 * - company_name：公司名
 * 另有 setCustomAttributes({ key: value }) 可传任意自定义键值，会在客服端「联系人属性」等位置展示。
 * 参考：https://www.chatwoot.com/hc/user-guide/articles/1677587234
 */
/**
 * 从后端拉取用户信息并同步到 Chatwoot，不依赖 localStorage（避免被篡改）
 * @param preFetchedUser 若已调用过 getUserInfo 可直接传入，避免重复请求
 */
export async function syncChatwootUser(
  preFetchedUser?: User | null
): Promise<void> {
  if (!isChatwootEnabled || !window.$chatwoot?.setUser) return;
  try {
    console.info("[chatwoot] syncChatwootUser:start", tokenDebugSnapshot());
    const user =
      preFetchedUser !== undefined
        ? preFetchedUser
        : ((await getUserInfo()) as User | undefined);
    if (!user) return;
    const identifier =
      user.userNo != null && String(user.userNo).trim()
        ? String(user.userNo).trim()
        : "";
    const name =
      (user.userName && String(user.userName).trim()) ||
      (user.userNo && String(user.userNo).trim()) ||
      "";
    if (!identifier || !name) {
      console.warn("[chatwoot] syncChatwootUser:missing-identity", {
        identifierLength: identifier.length,
        nameLength: name.length,
      });
      return;
    }
    const attrs: {
      name: string;
      avatar_url?: string;
      company_name?: string;
      description?: string;
    } = { name };
    if (user.userImage && typeof user.userImage === "string") {
      attrs.avatar_url = user.userImage;
    }
    if (user.firmName && typeof user.firmName === "string") {
      attrs.company_name = user.firmName;
    }
    if (user.description && typeof user.description === "string") {
      attrs.description = user.description;
    }
    window.$chatwoot.setUser(identifier, attrs);
    console.info("[chatwoot] syncChatwootUser:success", {
      identifierLength: identifier.length,
      hasAvatar: !!attrs.avatar_url,
    });
  } catch (error) {
    console.warn("[chatwoot] syncChatwootUser:failed", {
      ...tokenDebugSnapshot(),
      error,
    });
  }
}

/**
 * 关闭 Chatwoot 客服窗口。在 Electron 下路由切换前调用，可减少「打开客服后切页」导致的渲染进程崩溃。
 */
export function closeChatwoot(): void {
  if (
    isChatwootEnabled &&
    window.$chatwoot &&
    typeof window.$chatwoot.toggle === "function"
  ) {
    try {
      window.$chatwoot.toggle("close");
    } catch {
      // 忽略关闭时的异常
    }
  }
}

/**
 * 完整卸载 Chatwoot SDK（用于 Electron 路由切换前）
 * - close 只会隐藏窗口，不会卸载 SDK；
 * - 这里会清理 DOM/script/global，避免「打开过一次后」SDK 常驻导致后续切页崩溃。
 */
export function teardownChatwoot(): void {
  removeReadySyncHandler();
  try {
    if (window.$chatwoot && typeof window.$chatwoot.toggle === "function") {
      window.$chatwoot.toggle("close");
    }
    if (window.$chatwoot && typeof window.$chatwoot.reset === "function") {
      window.$chatwoot.reset();
    }
  } catch (error) {
    console.warn("[chatwoot] teardown close/reset failed", error);
  }

  removeChatwootDomArtifacts();

  if (sdkScriptEl?.parentNode) {
    sdkScriptEl.parentNode.removeChild(sdkScriptEl);
  }
  sdkScriptEl = null;
  document
    .querySelectorAll(
      `script[data-chatwoot="sdk"], script[src="${BASE_URL}/packs/js/sdk.js"]`
    )
    .forEach((el) => el.remove());

  try {
    delete (window as unknown as Record<string, unknown>).$chatwoot;
    delete (window as unknown as Record<string, unknown>).chatwootSDK;
    delete (window as unknown as Record<string, unknown>).chatwootSettings;
  } catch {
    // 某些环境下 delete 可能失败，不影响后续重新注入
  }

  loadStarted = false;
}

/**
 * 打开 Chatwoot 客服窗口（由我们的入口调用，如悬浮图标、右侧栏客服）
 * 官方文档：window.$chatwoot.toggle("open") / toggle("close")；建议在 chatwoot:ready 后再调用。
 */
export function openChatwoot(): void {
  // 改为懒加载：仅在用户主动打开客服时才注入 SDK，避免登录流程引入第三方脚本风险
  loadChatwoot();
  const open = async () => {
    try {
      // 先同步用户信息，确保 identifier/name 生效后再打开窗口，避免随机访客与新会话
      await syncChatwootUser();
      if (window.$chatwoot && typeof window.$chatwoot.toggle === "function") {
        window.$chatwoot.toggle("open");
      }
    } catch (error) {
      console.error("[chatwoot] failed to open widget", error);
    }
  };
  if (window.$chatwoot && typeof window.$chatwoot.toggle === "function") {
    open();
  } else {
    window.addEventListener("chatwoot:ready", open, { once: true });
  }
}
